import React from 'react';

import Accordion from '../../../resuable-ui/accordion';
import DeleteIconSvg from '../../../svgs/DeleteIconSvg';
import RightArrowSmSvg from '../../../svgs/RightArrowSmSvg';

import { formatAmount } from '../../../../utils/funcs';
import styles from './index.module.scss';
import { DeleteModalContent } from './../record-modals/delete/';

import { useCart } from '../../../../hooks/useCart';
import { useDeleteFromCart } from './../../../../integrations/react_query/mutations';
import { CartProps, IDeleteCartItem, EQuerykeys, ECart } from '../../../../models';
import useDebounce from '../../../../hooks/useDebounce';
import { useAppSelector } from '../../../../store/storeConfig';
import { getSession, saveSession } from '../../../../utils/storage';
import { useQueryClient } from 'react-query';
import { triggerToast } from '../../../../toast';
import PriceMarkupModal from '../../../../components/sales-terminal/records/price-markup/PriceMarkupModal';
import { PlusIcon } from '../../../../components/svgs/PlusIcon';
import { SubtractIcon } from '../../../../components/svgs/SubtractIcon';

type THeader = {
	quantity: number;
	name: string;
	price: number | null;
	setExpanded: (v: string) => void;
	id: string;
	expanded: any;
	setShow: (arg: boolean) => void;
	setItem: (arg: { name: string; id: string; refCartId: string }) => void;
	handleDelete: (arg: IDeleteCartItem) => void;
	refCartId: string;
	openPriceMarkup?: (p: { productId: string; name?: string; price: number }) => void;
};

const RenderHeader = React.forwardRef(
	(
		{ quantity, name, price, expanded, setExpanded, id, setShow, setItem, refCartId, openPriceMarkup }: THeader,
		ref: any
	) => {
		const { currentProductId } = useAppSelector((state) => state.productSlice);

		const isOpen = id === expanded;

		const handleExpand = (e: any) => {
			if (!(e.target.id === 'delete-icon')) {
				setExpanded(isOpen ? '' : id);
			}
		};

		const handlePriceClick = (e: React.MouseEvent) => {
			e.stopPropagation();
			if (!openPriceMarkup) return;
			openPriceMarkup({ productId: id, name, price: Number(price ?? 0) });
		};

		React.useEffect(() => {
			if (ref.current && currentProductId) {
				ref.current.scrollIntoView({
					// behavoir: "smooth",
					block: 'nearest',
				});
			}
		}, [currentProductId]);

		return (
			<section
				ref={currentProductId === id ? ref : null}
				className={`${styles.header} w-100 d-flex align-items-center justify-content-between`}
				onClick={handleExpand}
			>
				<div className={`${styles.name} text-capitalize line-clamp-1`}>{name}</div>

				<div className={`d-flex ${styles.quantityContainer}`} role="button" onClick={handlePriceClick}>
					<span className={styles.times}>x</span>
					<span className={`${styles.quantity} line-clamp-1`}>{quantity || 0}</span>
				</div>

				<div className={`d-flex align-items-center justify-content-between ${styles.priceContainer}`}>
					<div
						style={{ flex: 8 }}
						className={`py-2 ${styles.price} line-clamp-1`}
						role="button"
						onClick={handlePriceClick}
					>
						{formatAmount(Number(price))}
					</div>
					<div
						style={{ flex: 2, alignItems: 'center', alignSelf: 'center' }}
						id="delete-icon"
						onClick={() => {
							setShow(true);
							setItem({ name, id, refCartId });
						}}
					>
						<DeleteIconSvg id="delete-icon" />
					</div>
					<div style={{ flex: 2 }} className={isOpen ? styles.rotate : styles.transit}>
						<RightArrowSmSvg />
					</div>
				</div>
			</section>
		);
	}
);

type TBody = {
	quantity: number;
	name: string;
	price: number | null;
	sellingPrice: number | null;
	discount: number | null;
	productId: string;
	variantId: number | null;
	unitCost: number | null;
	refCartId: string;
	handleAddToCart: (v: any) => void;
	computedDiscount?: string;
};

const RenderBody = ({
	quantity,
	variantId,
	price,
	discount,
	productId,
	handleAddToCart,
	refCartId,
	sellingPrice,
	computedDiscount,
}: TBody) => {
	const [changeQuantity, setChangeQuantity] = React.useState('');

	const debounceQuantity = useDebounce(changeQuantity, 50);

	React.useEffect(() => {
		if (changeQuantity === '') {
			return;
		}

		if (changeQuantity === 'e') {
			handleAddToCart({
				quantity: '',
				_id: productId,
				variantId,
				refCartId,
			});
			return;
		}

		handleAddToCart({
			quantity: Number(changeQuantity),
			_id: productId,
			variantId,
			refCartId,
		});
	}, [debounceQuantity]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let value = e.target.value;

		if (isNaN(Number(value))) {
			return;
		}

		value = value.replace(/^0+(?=\d)/, '');

		setChangeQuantity(value === '' ? 'e' : value);
	};

	return (
		<section className={`${styles.bodyWrapper}`}>
			<div className={`${styles.tableBodyHeader} d-flex align-items-center justify-content-between`}>
				<div style={{ flex: 4 }}>Price (&#8358;)</div>
				<div style={{ flex: 4 }}>Qty</div>
				<div style={{ flex: 4 }}>Discount</div>
			</div>

			<div className={`d-flex align-items-center justify-content-between ${styles.tableBodyWrapper}`}>
				<div style={{ flex: 4 }}>{formatAmount(price ?? 0)}</div>
				<div className={`d-flex justify-content-between`} style={{ flex: 4 }}>
					<button
						style={{ flex: 3 }}
						type="button"
						aria-label="subtract"
						onClick={() =>
							handleAddToCart({
								quantity: Math.max((quantity ?? 0) - 1, 1),
								_id: productId,
								variantId,
								refCartId,
							})
						}
					>
						<SubtractIcon />
					</button>
					<input
						style={{ flex: 3 }}
						value={changeQuantity === 'e' ? quantity : changeQuantity === '' ? quantity : changeQuantity || quantity}
						onChange={handleChange}
						className="text-center"
					/>
					<button
						style={{ flex: 3 }}
						type="button"
						aria-label="add"
						onClick={() =>
							handleAddToCart({
								quantity: (quantity ?? 0) + 1,
								_id: productId,
								variantId,
								refCartId,
							})
						}
					>
						<PlusIcon />
					</button>
				</div>
				<div style={{ flex: 4 }}>
					{computedDiscount ??
						(() => {
							const originalPrice = sellingPrice ?? 0;
							const currentPrice = price ?? 0;
							const priceDiscount = originalPrice - currentPrice;

							if (priceDiscount > 0) {
								const discountPercentage = (priceDiscount / originalPrice) * 100;
								return `${formatAmount((quantity || 1) * priceDiscount)} (${discountPercentage.toFixed(1)}%)`;
							} else if (currentPrice > originalPrice) {
								return '0%';
							}

							return discount
								? typeof discount === 'number' && discount > 1
									? formatAmount(discount)
									: `${discount}%`
								: '0%';
						})()}
				</div>
			</div>
		</section>
	);
};

export const CartItems = ({ data }: { data: CartProps }) => {
	const { handleAddToCart } = useCart('change');

	const queryClient = useQueryClient();

	const [overrides, setOverrides] = React.useState<Record<string, number>>({});

	React.useEffect(() => {
		const active = queryClient.getQueryData([EQuerykeys.cart, ECart.active]) as CartProps | undefined;
		const refCartId = active?.['_id'];
		if (refCartId) {
			const key = `price-overrides-${refCartId}`;
			const sessionOverrides = (getSession(key) as Record<string, number>) || {};
			setOverrides(sessionOverrides);
		}
	}, [data]);

	const { currentProductId } = useAppSelector((state) => state.productSlice);

	const { mutate: mutateDelete } = useDeleteFromCart();

	const [expanded, setExpanded] = React.useState<string>('');

	const [show, setShow] = React.useState(false);

	const handleDelete = (arg: IDeleteCartItem) => {
		mutateDelete(arg, {
			onSuccess: () => {
				try {
					const refCartId = (arg as any).refCartId || (arg as any).cartId || '';
					const productId = (arg as any).productId || (arg as any)._id || '';
					if (!refCartId || !productId) return;
					const key = `price-overrides-${refCartId}`;
					const current = (getSession(key) as Record<string, number>) || {};
					if (current && Object.prototype.hasOwnProperty.call(current, productId)) {
						delete current[productId];
						saveSession(key, current);
						queryClient.invalidateQueries([EQuerykeys.cart, ECart.active]);
					}
				} catch (e) {
					// swallow errors silently; don't block deletion UX
				}
			},
		});
	};

	const [item, setItem] = React.useState<{
		name: string;
		id: string;
		refCartId: string;
	}>({
		name: '',
		id: '',
		refCartId: '',
	});

	const cartRef = React.useRef<any>(null);

	// price markup modal state
	const [showPriceModal, setShowPriceModal] = React.useState(false);
	const [selectedProductForMarkup, setSelectedProductForMarkup] = React.useState<{
		productId: string;
		name?: string;
		price: number;
	} | null>(null);

	const handleApplyOverride = (productId: string, price: number) => {
		setOverrides((s) => ({ ...s, [productId]: price }));
		const active = queryClient.getQueryData([EQuerykeys.cart, ECart.active]) as CartProps | undefined;
		const refCartId = active?._id;
		if (refCartId) {
			const key = `price-overrides-${refCartId}`;
			const current = (getSession(key) as Record<string, number>) || {};
			current[productId] = price;
			saveSession(key, current);
		}
		queryClient.invalidateQueries([EQuerykeys.cart, ECart.active]);
	};

	const openPriceMarkup = (p: { productId: string; name?: string; price: number }) => {
		const active = queryClient.getQueryData([EQuerykeys.cart, ECart.active]) as CartProps | undefined;
		if (!active?.customer) {
			return triggerToast({ type: 'info', message: 'Select a customer before adjusting price.' });
		}

		const productInData = data?.products?.find((x) => x.productId === p.productId);
		const trueOriginal = productInData?.sellingPrice ?? p.price ?? 0;

		setSelectedProductForMarkup({ productId: p.productId, name: p.name, price: Number(trueOriginal) });
		setShowPriceModal(true);
	};

	React.useEffect(() => {
		// const firstCartItem = (data || [])?.[0]?.productId;

		setExpanded(currentProductId as any);

		// if (cartRef.current) {
		//   cartRef.current.scrollTo({
		//     top: 0,
		//     behaviour: "smooth",
		//   });
		// }
	}, [currentProductId, data.products.length]);

	return (
		<>
			<DeleteModalContent {...{ show, item, onClose: setShow, handleDelete }} />

			<section className={`${styles.wrapper}`}>
				{data?.products?.map(
					({ discount, productId, productName, quantity, refCartId, sellingPrice, unitCost, variantId }) => {
						return (
							<Accordion
								id={productId}
								key={productId}
								expanded={expanded}
								setExpanded={setExpanded}
								headerContext={() => (
									<RenderHeader
										{...{
											quantity,
											name: productName,
											price: overrides[productId] ?? sellingPrice,
											expanded,
											unitCost,
											setExpanded,
											id: productId,
											refCartId: refCartId as string,
											setShow,
											setItem,
											handleDelete,
											openPriceMarkup: openPriceMarkup,
										}}
										ref={cartRef}
									/>
								)}
								bodyContext={() => (
									<RenderBody
										{...{
											quantity,
											name: productName,
											price: overrides[productId] ?? sellingPrice,
											sellingPrice,
											discount,
											unitCost,
											productId,
											handleAddToCart,
											variantId,
											refCartId: refCartId as string,
										}}
									/>
								)}
							/>
						);
					}
				)}
			</section>

			{selectedProductForMarkup && (
				<PriceMarkupModal
					show={showPriceModal}
					setShow={(v: boolean) => setShowPriceModal(v)}
					productId={selectedProductForMarkup.productId}
					productName={selectedProductForMarkup.name}
					originalPrice={selectedProductForMarkup.price}
					onApply={(p: number) => handleApplyOverride(selectedProductForMarkup.productId, p)}
				/>
			)}
		</>
	);
};
